import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Heart, Star } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function Matches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await fetch(`${API}/matches`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      // Non-critical: will retry
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-8" data-testid="matches-title">
          Your Matches
        </h1>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="brutal-card animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-200 border-2 border-black"></div>
                  <div className="flex-1">
                    <div className="h-6 bg-gray-200 w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 w-48"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : matches.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="brutal-card text-center py-16"
            data-testid="no-matches-card"
          >
            <Heart className="w-16 h-16 mx-auto mb-4 text-[#FF66B2]" />
            <h2 className="text-2xl font-bold mb-2">No matches yet</h2>
            <p className="text-[#4A4A4A] mb-6">Start swiping to find your match!</p>
            <button
              onClick={() => navigate("/discover")}
              className="brutal-btn-primary"
              data-testid="go-discover-btn"
            >
              Start Discovering
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {matches.map((match, index) => (
              <motion.div
                key={match.match_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/chat/${match.match_id}`)}
                className={`match-card ${match.has_superlike ? "border-[#FFD700] border-4" : ""}`}
                data-testid={`match-card-${match.match_id}`}
              >
                {/* Avatar */}
                <div className="relative">
                  <div className="w-16 h-16 border-2 border-black bg-gray-200 flex-shrink-0 overflow-hidden">
                    {match.user.picture ? (
                      <img
                        src={match.user.picture}
                        alt={match.user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl font-black">
                        {match.user.name?.[0] || "?"}
                      </div>
                    )}
                  </div>
                  {/* Online indicator */}
                  {match.user.is_online && (
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#00CC99] border-2 border-black rounded-full"></span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold truncate">{match.user.name}</h3>
                    {match.has_superlike && (
                      <Star className="w-5 h-5 text-[#FFD700] flex-shrink-0" fill="currentColor" />
                    )}
                    {match.user.is_online && (
                      <span className="text-xs text-[#00CC99] font-bold">Online</span>
                    )}
                  </div>
                  {match.last_message ? (
                    <p className="text-[#4A4A4A] text-sm truncate">
                      {match.last_message.sender_id === user?.user_id ? "You: " : ""}
                      {match.last_message.content}
                    </p>
                  ) : (
                    <p className="text-[#FF4D4D] text-sm font-medium">Say hi! 👋</p>
                  )}
                </div>

                {/* Message Icon */}
                <MessageCircle className="w-6 h-6 text-[#4A4A4A] flex-shrink-0" />
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
